import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../tokens';
import './ShareProfile.css';

// Origen canónico para los enlaces compartidos. En producción se fija con
// REACT_APP_PUBLIC_URL (https://troly.club); en local cae al origen actual.
const PUBLIC_ORIGIN = (process.env.REACT_APP_PUBLIC_URL || '').replace(/\/$/, '');

/**
 * Botón de compartir del perfil público: WhatsApp, Facebook, correo y copiar
 * enlace. Funciona igual para visitantes sin registro (el enlace apunta al
 * perfil público, no a una ruta protegida).
 */
export default function ShareProfile({ profileId, displayName }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onClickAway = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClickAway);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClickAway);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!copied) return undefined;
    const id = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(id);
  }, [copied]);

  const origin = PUBLIC_ORIGIN || (typeof window !== 'undefined' ? window.location.origin : '');
  const url = `${origin}/u/${profileId}`;
  const text = t('share.message', { name: displayName });

  const waHref = `https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`;
  const fbHref = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
  const mailHref = `mailto:?subject=${encodeURIComponent(t('share.email_subject', { name: displayName }))}&body=${encodeURIComponent(`${text}\n\n${url}`)}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      // Fallback para navegadores sin Clipboard API (o contexto no seguro).
      const el = document.createElement('textarea');
      el.value = url;
      el.setAttribute('readonly', '');
      el.style.position = 'absolute';
      el.style.left = '-9999px';
      document.body.appendChild(el);
      el.select();
      try { document.execCommand('copy'); setCopied(true); } catch { /* noop */ }
      document.body.removeChild(el);
    }
  };

  // En móvil, si el navegador expone la hoja nativa de compartir, la usamos
  // directamente: es más rápida y ya incluye WhatsApp, correo y redes.
  const openShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: `${displayName} — Troly`, text, url });
        return;
      } catch {
        // Cancelado o no permitido → seguimos con el menú propio.
      }
    }
    setOpen((v) => !v);
  };

  return (
    <div className="share" ref={wrapRef}>
      <button
        type="button"
        className="share__trigger"
        onClick={openShare}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('share.action')}
      >
        {Icon.share(14)} {t('share.action')}
      </button>

      {open && (
        <div className="share__menu" role="menu">
          <span className="share__menu-title">{t('share.title')}</span>

          <a
            className="share__item"
            role="menuitem"
            href={waHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
          >
            <span className="share__item-icon">{Icon.whatsapp(15)}</span>
            WhatsApp
          </a>

          <a
            className="share__item"
            role="menuitem"
            href={fbHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
          >
            <span className="share__item-icon">{Icon.facebook(15)}</span>
            Facebook
          </a>

          <a
            className="share__item"
            role="menuitem"
            href={mailHref}
            onClick={() => setOpen(false)}
          >
            <span className="share__item-icon">{Icon.mail(15)}</span>
            {t('share.email')}
          </a>

          <button
            type="button"
            className={`share__item${copied ? ' share__item--done' : ''}`}
            role="menuitem"
            onClick={copyLink}
          >
            <span className="share__item-icon">{Icon.link(15)}</span>
            {copied ? t('share.copied') : t('share.copy_link')}
          </button>
        </div>
      )}
    </div>
  );
}
